import { settings } from './settings.js';
import { tapDist } from './util.js';

// Sampled video preview playback: instead of playing a clip start-to-finish,
// hop through a handful of short segments spread across the duration so a
// long video's content is visible at a glance. Used everywhere videos
// auto-preview (side panel, grid hover-play).
//
// playSample(videoEl, {segments, segSeconds, rate}):
//   - split the duration into `segments` even parts, play the first
//     `segSeconds` of each (video-timeline seconds, independent of `rate`)
//     at `playbackRate = rate`, then loop back to the first segment;
//   - if the whole clip is shorter than segments*segSeconds, sampling would
//     cover the whole thing anyway — just loop the full clip at `rate`;
//   - returns a stop() that detaches everything, safe to call even if the
//     element is about to be reused for a different sample (independent
//     closure state, no shared/global timers — advancement is driven by the
//     video's own 'timeupdate'/'ended' events, not setTimeout, so there's
//     nothing left running once stop() removes the listeners).
export function playSample(videoEl, { segments = 5, segSeconds = 2, rate = 2 } = {}) {
  let stopped = false;
  let segStarts = null;   // null while whole-clip looping (no sampling needed)
  let curSeg = 0;

  function seek(t) {
    try { videoEl.currentTime = t; } catch {}
  }

  // Advance to the next segment (wrap to the first past the last). Only
  // called from the two listeners below, both already gated on `stopped`.
  function advance() {
    curSeg = (curSeg + 1) % segments;
    seek(segStarts[curSeg]);
  }

  function onTimeUpdate() {
    if (stopped) return;
    if (videoEl.currentTime >= segStarts[curSeg] + segSeconds) advance();
  }
  // Belt-and-braces: if a segment runs to the literal end of the file before
  // timeupdate's next tick catches it (e.g. uneven division), 'ended' still
  // sends us back into the loop instead of the video just stopping. Only
  // wired up in sampling mode — whole-clip mode uses native `loop` instead,
  // which never fires 'ended' at all.
  function onEnded() {
    if (stopped) return;
    advance();
    videoEl.play().catch(() => {});
  }

  function start() {
    if (stopped) return;
    videoEl.playbackRate = rate;
    const dur = videoEl.duration;
    if (!dur || !isFinite(dur)) return;   // shouldn't happen post-metadata, but be safe
    if (dur <= segments * segSeconds) {
      videoEl.loop = true;   // native loop covers it; no sampling listeners needed
    } else {
      segStarts = Array.from({ length: segments }, (_, i) => i * dur / segments);
      curSeg = 0;
      videoEl.loop = false;
      seek(segStarts[0]);
      videoEl.addEventListener('timeupdate', onTimeUpdate);
      videoEl.addEventListener('ended', onEnded);
    }
    videoEl.play().catch(() => {});
  }

  if (videoEl.readyState >= 1 && isFinite(videoEl.duration)) start();   // HAVE_METADATA already
  else videoEl.addEventListener('loadedmetadata', start, { once: true });

  return function stop() {
    stopped = true;
    videoEl.removeEventListener('loadedmetadata', start);
    videoEl.removeEventListener('timeupdate', onTimeUpdate);
    videoEl.removeEventListener('ended', onEnded);
  };
}

// Shared by every muted auto-preview context (side panel, grid hover-play, and any
// future one): read the file, object-URL it into `videoEl`, and start
// sampling. `stillWanted()` is re-checked after the (async) file read so a
// focus/hover change mid-await doesn't wire up a video nobody asked for
// anymore — each caller passes its own notion of "still the current target"
// (preview.js: focused cell; hoverplay.js: still-hovered cell). Returns null in
// that case, otherwise `{ url, stop }` — the caller owns revoking `url` and
// calling `stop()` on teardown (same contract as calling playSample directly).
export async function loadSampledVideo(item, videoEl, sampleOpts, stillWanted) {
  let file;
  try { file = await item.handle.getFile(); } catch { return null; }
  if (stillWanted && !stillWanted()) return null;
  const url = URL.createObjectURL(file);
  videoEl.src = url;
  return { url, stop: playSample(videoEl, sampleOpts) };
}

// Custom player chrome for a <video> with no native controls: a thin visual
// scrub strip (~5px) along the bottom of `host` (absolutely positioned, so it
// overlays without affecting layout — host just needs `position` set, which
// both callers already have: viewerStage and a preview .pslot). On mouse
// hover or touch-down it grows (~34px) revealing a time readout and a mute
// toggle; click/drag on the strip seeks (pointer capture — works with touch).
// `bar` itself (the pointer-event target) is ALWAYS a generous ~34px-tall hit
// zone regardless of the visual `track`'s collapsed/grown height — a
// 5px-tall touch target is nearly impossible to grab without a second,
// separate precision tap.
//
// opts.tapToggle: tapping the video itself (not the strip) toggles
// play/pause — wanted in the viewer, but NOT in preview slots where a tap
// already means select/double-tap-open and must keep meaning that.
// opts.hoverScrub: preview-only. A bare mouse hover over the strip (no
// button held) seeks+plays live from the hovered position; opts.onScrubStart
// /onScrubEnd bracket that so the caller can pause/resume its own sampler
// (loadSampledVideo) around it. The viewer has no sampler, so it only seeks
// on an actual click/drag, never on passive hover.
//
// Every pointer event on the scrub strip itself is stopped from bubbling — the
// viewer's pan/pinch/swipe tracking and the side panel's own swipe/tap
// handling both listen higher up (viewerStage / #panel) and must not also
// react to a gesture that's really aimed at the scrub strip.
export function createPlayer(host, videoEl, { muted = false, tapToggle = true, hoverScrub = false, onScrubStart, onScrubEnd } = {}) {
  videoEl.controls = false;
  videoEl.muted = muted;

  const bar = document.createElement('div');
  bar.className = 'vplayer-bar';
  // `track` is the visual strip (thin/grown, background); `bar` itself is
  // just the (always generously sized) pointer target — see the CSS.
  const track = document.createElement('div'); track.className = 'vplayer-track';
  const fill = document.createElement('div'); fill.className = 'vplayer-fill';
  track.appendChild(fill);
  const time = document.createElement('span'); time.className = 'vplayer-time';
  const muteBtn = document.createElement('button');
  muteBtn.type = 'button'; muteBtn.className = 'vplayer-mute';
  bar.append(track, time, muteBtn);
  host.appendChild(bar);

  const fmt = (s) => {
    if (!isFinite(s)) return '0:00';
    s = Math.max(0, Math.round(s));
    return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  };
  // scrubFrac: while non-null, overrides currentTime for rendering — set the
  // instant a drag/hover-scrub moves and cleared once the video has actually
  // settled there (see settleIfIdle). This decouples the fill/time visual
  // from the (slow, real) seek completing — without it the visual would only
  // ever move as fast as the decoder-bound seek, making scrubbing feel sluggish.
  let scrubFrac = null;
  function render() {
    const d = videoEl.duration;
    const cur = scrubFrac !== null ? scrubFrac * (d || 0) : videoEl.currentTime;
    fill.style.width = (d ? cur / d * 100 : 0) + '%';
    // The text nodes are invisible (opacity 0) and non-interactive while thin
    // — skip writing them then. 'timeupdate' fires continuously during
    // playback (and on every scrub-seek), so this is a real hot path; the fill
    // above still needs to stay current even while thin, since it's what a
    // fresh hover sees immediately.
    if (!bar.classList.contains('grow')) return;
    time.textContent = `${fmt(cur)} / ${fmt(d)}`;
    muteBtn.textContent = videoEl.muted ? '\u{1F507}' : '\u{1F50A}';
  }
  videoEl.addEventListener('timeupdate', render);
  videoEl.addEventListener('loadedmetadata', render);
  videoEl.addEventListener('volumechange', render);
  render();

  // pointerup, not 'click': a real touch tap does get a
  // synthesized compatibility click, but relying on it here would mean a
  // second, unrelated tap-detection path from the one every other control in
  // this file (and the rest of the app) uses — and synthetic PointerEvent
  // tests, which don't get a synthesized click at all, couldn't drive it.
  muteBtn.addEventListener('pointerdown', (e) => e.stopPropagation());
  muteBtn.addEventListener('pointerup', (e) => { e.stopPropagation(); videoEl.muted = !videoEl.muted; });

  const fracAt = (clientX) => {
    const r = bar.getBoundingClientRect();
    return r.width ? Math.min(1, Math.max(0, (clientX - r.left) / r.width)) : 0;
  };

  // Real seeks are coalesced via videoEl.seeking, which the spec sets
  // synchronously the moment currentTime/fastSeek is assigned and clears right
  // before 'seeked' fires — so it's a reliable "is one in flight" gate with no
  // hand-rolled flag/race to keep in sync. While one is in flight, a newer
  // request just overwrites `pendingSeek` (latest-wins, never queued) and gets
  // issued the instant 'seeked' lands. Mid-drag uses fastSeek() (keyframe-
  // snapped, far cheaper than an exact decode) when available; `exact` forces
  // a precise currentTime seek, used only for the final position on release.
  let pendingSeek = null;
  function performSeek(f, exact) {
    const d = videoEl.duration;
    if (!d || !isFinite(d)) return;
    if (!exact && videoEl.fastSeek) { try { videoEl.fastSeek(f * d); return; } catch {} }
    try { videoEl.currentTime = f * d; } catch {}
  }
  function requestSeek(f, exact) {
    if (videoEl.seeking) { pendingSeek = { f, exact }; return; }
    performSeek(f, exact);
  }
  function onSeeked() {
    if (pendingSeek) { const p = pendingSeek; pendingSeek = null; performSeek(p.f, p.exact); }
    else settleIfIdle();
  }
  videoEl.addEventListener('seeked', onSeeked);
  // The fill/time visual updates immediately (render, synchronous) regardless
  // of how long the underlying seek takes; the seek itself is fire-and-forget
  // via requestSeek's coalescing.
  function scrubTo(clientX, exact) {
    const f = fracAt(clientX);
    scrubFrac = f;
    render();
    requestSeek(f, exact);
  }
  // scrubFrac stays authoritative (visual doesn't snap back to a stale
  // currentTime) until every input driving it has stopped AND the last
  // requested seek has actually landed — called after each of those
  // conditions changes (hover end, drag end, seek completion).
  function settleIfIdle() {
    if (!scrubbing && !hovering && !videoEl.seeking && !pendingSeek) { scrubFrac = null; render(); }
  }

  // hoverScrub start/end is gated identically from four different pointer
  // events (down/move for start, leave/up-or-cancel for end) — named once so
  // the "hoverScrub gates hovering" invariant lives in one place.
  const startHover = () => { if (hoverScrub && !hovering) { hovering = true; onScrubStart?.(); } };
  const endHover = () => { if (hoverScrub && hovering) { hovering = false; onScrubEnd?.(); settleIfIdle(); } };

  // Touch has no hover, so "grow" can only start on pointerdown and there's no
  // pointerleave to end it on. Collapsing it back the instant that same finger
  // lifts would make the mute button/time readout impossible
  // to ever reach on touch: they only exist while the finger is already down,
  // and pointer capture (below) keeps that same finger's whole gesture — drag
  // included — pinned to scrubbing, so it can never itself "land on" mute.
  // Instead, a touch grow lingers for TOUCH_GROW_MS after release, giving a
  // second, separate tap a real window to hit mute/time; any further bar
  // touch (another scrub, or the mute tap itself via its own pointerdown
  // stopPropagation not reaching here) resets the timer.
  const TOUCH_GROW_MS = 3000;
  let touchHideTimer = null;
  function cancelTouchHide() { clearTimeout(touchHideTimer); touchHideTimer = null; }
  function scheduleTouchHide() {
    cancelTouchHide();
    touchHideTimer = setTimeout(() => { bar.classList.remove('grow'); endHover(); touchHideTimer = null; }, TOUCH_GROW_MS);
  }

  let scrubbing = false, hovering = false;
  bar.addEventListener('pointerenter', (e) => {
    if (e.pointerType !== 'mouse') return;
    cancelTouchHide();   // a real mouse taking over must not have a stale touch timer yank .grow out from under it
    bar.classList.add('grow');
    render();
  });
  bar.addEventListener('pointerleave', (e) => {
    if (e.pointerType !== 'mouse' || scrubbing) return;
    bar.classList.remove('grow');
    endHover();
  });
  // Scrub samples (visual render + rect read + seek together) are coalesced
  // to one per animation frame, since pointermove can fire well above
  // video-frame rate and the rect read + seek cost real layout/decoder work.
  // Only pointerdown's first sample applies immediately (see below).
  let pendingX = null;
  function flushSeek() {
    if (pendingX !== null) { scrubTo(pendingX, false); pendingX = null; }
    rafScheduled = false;
  }
  let rafScheduled = false;
  function scheduleSeek(clientX) {
    pendingX = clientX;
    if (!rafScheduled) { rafScheduled = true; requestAnimationFrame(flushSeek); }
  }
  bar.addEventListener('pointerdown', (e) => {
    e.stopPropagation();
    cancelTouchHide();
    bar.classList.add('grow');
    scrubbing = true;
    try { bar.setPointerCapture(e.pointerId); } catch {}
    startHover();
    scrubTo(e.clientX, false);   // first sample applies immediately (incl. render()), not next frame
  });
  bar.addEventListener('pointermove', (e) => {
    if (scrubbing) { scheduleSeek(e.clientX); return; }
    if (hoverScrub && e.pointerType === 'mouse') { startHover(); scheduleSeek(e.clientX); }
  });
  function endScrub(e) {
    if (!scrubbing) return;
    scrubbing = false;
    pendingX = null;
    scrubTo(e.clientX, true);   // exact — land precisely where the pointer let go, not wherever the last coalesced fastSeek was
    if (e.pointerType !== 'mouse') {
      scheduleTouchHide();
      endHover();   // ends hovering here (if hoverScrub) and settles — no separate settleIfIdle() needed
    }
    // Mouse: pointer is still physically over the strip after releasing —
    // stay grown/hovering until pointerleave actually fires (endHover then).
    // Either way, videoEl.seeking is now true (the exact seek just issued
    // synchronously) or about to be, so settling happens via onSeeked()'s own
    // settleIfIdle() once that seek (and anything it coalesces) actually lands.
  }
  bar.addEventListener('pointerup', (e) => { e.stopPropagation(); endScrub(e); });
  bar.addEventListener('pointercancel', (e) => { e.stopPropagation(); endScrub(e); });

  // Single source of truth for play/pause toggling — the internal tap
  // handler below and a caller's own key handler (the viewer's Space key)
  // both just call this instead of each re-deciding paused ? play() : pause().
  function toggle() {
    if (videoEl.paused) videoEl.play().catch(() => {}); else videoEl.pause();
  }

  // Tap-to-pause on the video itself. pointerup + the shared tapDist check
  // (not 'click') matches how the rest of the app disambiguates tap vs
  // drag/scroll, and is what synthetic-PointerEvent tests can drive —
  // browsers don't reliably synthesize a compatibility 'click' for those.
  //
  // Listens on `host`, NOT `videoEl`: the viewer's own stage
  // wiring calls `viewerStage.setPointerCapture()` on the down that starts a
  // pan/swipe (viewer.js), and `host` IS `viewerStage` there. Per the Pointer
  // Events spec, once a pointer is captured its subsequent events retarget to
  // the capturing element and dispatch only walks that element's ancestor
  // chain — a listener on `videoEl`, a DESCENDANT of the capturing element,
  // never sees them at all. That's silent and device-dependent: synthetic
  // PointerEvents dispatched straight at an element (as tests do) aren't tied
  // to a real active pointer, so `setPointerCapture` throws and is swallowed,
  // masking the bug — only a real touch/mouse down reliably captures and
  // exposes it. `host` is always either an ancestor of `videoEl` or `videoEl`
  // itself, so listening there works whether or not capture is in play.
  function onVideoTap(e) {
    if (tapDist(e) > 10) return;
    toggle();
  }
  if (tapToggle) host.addEventListener('pointerup', onVideoTap);

  return {
    toggle,
    destroy() {
      videoEl.removeEventListener('timeupdate', render);
      videoEl.removeEventListener('loadedmetadata', render);
      videoEl.removeEventListener('volumechange', render);
      videoEl.removeEventListener('seeked', onSeeked);
      if (tapToggle) host.removeEventListener('pointerup', onVideoTap);
      cancelTouchHide();
      bar.remove();
    },
  };
}

export async function mountVideoPreview(host, videoEl, item, { stillWanted }) {
  const sampleOpts = {
    segments: settings.get('vidSegments'),
    segSeconds: settings.get('vidSegSeconds'),
    rate: settings.get('vidRate'),
  };

  const r = await loadSampledVideo(item, videoEl, sampleOpts, stillWanted);
  if (!r) return null;

  let stopSample = r.stop;
  const player = createPlayer(host, videoEl, {
    muted: true, tapToggle: false, hoverScrub: true,
    onScrubStart: () => { stopSample?.(); stopSample = null; },
    onScrubEnd: () => { stopSample = playSample(videoEl, sampleOpts); },
  });

  return function teardown() {
    stopSample?.();
    player.destroy();
    if (r.url) URL.revokeObjectURL(r.url);
  };
}
